/**
 * The archive: everything done and dropped.
 *
 * Nothing is ever auto-purged, so this is the long record. It leads with
 * "days open" rather than the completion date, because how long something sat
 * there is the part that is actually informative months later.
 */

import { useCallback, useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import { ApiError, api } from '@/api/client'
import type { Category, Item, ItemStatus } from '@/api/types'
import { useToast } from '@/components/Toast'
import {
  EmptyState,
  ErrorState,
  LoadingBlock,
  PriorityPill,
  StatusNote,
  StatusPill,
} from '@/components/ui'
import { formatDateOnly, formatTimestamp, pluralize } from '@/lib/format'

const PAGE_SIZE = 50

export function ArchivePage() {
  const toast = useToast()
  const [params, setParams] = useSearchParams()

  const [items, setItems] = useState<Item[]>([])
  const [total, setTotal] = useState(0)
  const [categories, setCategories] = useState<Category[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [draftQuery, setDraftQuery] = useState(params.get('q') ?? '')
  const [shown, setShown] = useState(PAGE_SIZE)

  const q = params.get('q') ?? ''
  const status = (params.get('status') as ItemStatus | null) ?? null
  const categoryId = params.get('category')
  const from = params.get('from') ?? ''
  const to = params.get('to') ?? ''

  const setParam = useCallback(
    (key: string, value: string | null) => {
      const next = new URLSearchParams(params)
      next.delete(key)
      if (value) next.set(key, value)
      setParams(next, { replace: true })
    },
    [params, setParams],
  )

  useEffect(() => {
    if (draftQuery === q) return
    const timer = setTimeout(() => setParam('q', draftQuery || null), 250)
    return () => clearTimeout(timer)
  }, [draftQuery, q, setParam])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [archive, cats] = await Promise.all([
        api.archive.list({
          q: q || undefined,
          status: status ?? undefined,
          category_id: categoryId ? Number(categoryId) : undefined,
          completed_from: from || undefined,
          completed_to: to || undefined,
          limit: shown,
        }),
        api.categories.list(true),
      ])
      setItems(archive.items)
      setTotal(archive.total)
      setCategories(cats)
      setError(null)
    } catch (caught) {
      if (caught instanceof ApiError && !caught.isUnauthorized) setError(caught.message)
    } finally {
      setLoading(false)
    }
  }, [q, status, categoryId, from, to, shown])

  useEffect(() => {
    void load()
  }, [load])

  async function restore(item: Item) {
    try {
      await api.archive.restore(item.id)
      await load()
      toast.success(`Restored "${item.title}" to not started.`)
    } catch (caught) {
      toast.error(caught instanceof ApiError ? caught.message : 'Could not restore that item.')
    }
  }

  async function destroy(item: Item) {
    if (
      !window.confirm(
        `Permanently delete "${item.title}"? Its notes, files and history go with it. This cannot be undone.`,
      )
    )
      return
    try {
      await api.items.remove(item.id)
      await load()
      toast.success(`Deleted "${item.title}".`)
    } catch (caught) {
      toast.error(caught instanceof ApiError ? caught.message : 'Could not delete that item.')
    }
  }

  const filtered = Boolean(q || status || categoryId || from || to)

  return (
    <div className="flex flex-col gap-4">
      <section className="card p-3" aria-label="Archive filters">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <input
            type="search"
            className="field flex-1"
            placeholder="Search archived items and their notes..."
            value={draftQuery}
            onChange={(event) => setDraftQuery(event.target.value)}
            aria-label="Search the archive"
          />
          <select
            className="field w-full sm:w-40"
            value={status ?? ''}
            onChange={(event) => setParam('status', event.target.value || null)}
            aria-label="Outcome"
          >
            <option value="">Done and dropped</option>
            <option value="done">Done only</option>
            <option value="dropped">Dropped only</option>
          </select>
          <select
            className="field w-full sm:w-44"
            value={categoryId ?? ''}
            onChange={(event) => setParam('category', event.target.value || null)}
            aria-label="Category"
          >
            <option value="">All categories</option>
            {categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
        </div>

        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-ink-3">
          <label className="flex items-center gap-1">
            Completed from
            <input
              type="date"
              className="field w-auto px-2 py-1"
              value={from}
              onChange={(event) => setParam('from', event.target.value || null)}
            />
          </label>
          <label className="flex items-center gap-1">
            to
            <input
              type="date"
              className="field w-auto px-2 py-1"
              value={to}
              onChange={(event) => setParam('to', event.target.value || null)}
            />
          </label>
          {filtered && (
            <button
              type="button"
              className="font-medium text-navy hover:underline"
              onClick={() => setParams(new URLSearchParams(), { replace: true })}
            >
              Clear filters
            </button>
          )}
        </div>
      </section>

      <section className="card overflow-hidden">
        <div className="flex items-center justify-between border-b border-line px-3 py-2.5">
          <h1 className="text-sm font-semibold">Archive</h1>
          <span className="text-xs text-ink-3">
            {total} {pluralize(total, 'item')}
            {items.length < total && ` (showing ${items.length})`}
          </span>
        </div>

        {loading && items.length === 0 ? (
          <LoadingBlock label="Loading the archive" />
        ) : error ? (
          <ErrorState message={error} onRetry={() => void load()} />
        ) : items.length === 0 ? (
          <EmptyState title={filtered ? 'Nothing matches' : 'Nothing archived yet'}>
            {filtered
              ? 'No archived items match these filters.'
              : 'Items you finish or drop land here, with how long they were open. Nothing is ever purged automatically.'}
          </EmptyState>
        ) : (
          <ul className="m-0 list-none p-0">
            {items.map((item) => (
              <li
                key={item.id}
                className="group flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-line px-3 py-2.5 last:border-b-0 hover:bg-navy-softer"
              >
                {/* Full width on a phone so the title is readable: sharing a
                    flex line with the breadcrumb, pills and timestamp truncated
                    it to "Grad..." at 390px, which identifies nothing. */}
                <Link
                  to={`/items/${item.id}`}
                  className="w-full min-w-0 truncate font-medium text-ink-2 hover:text-navy hover:underline sm:w-auto sm:flex-1"
                >
                  {item.title}
                </Link>

                <span className="text-xs text-ink-3">
                  {item.subcategory.category.name} › {item.subcategory.name}
                </span>

                <StatusNote note={item.status_note} />

                <span
                  className="pill bg-surface-3 text-ink-2"
                  title={`Open from ${formatDateOnly(item.created_at.slice(0, 10))} until completion`}
                >
                  {item.days_open} {pluralize(item.days_open, 'day')} open
                </span>

                <StatusPill status={item.status} />
                <PriorityPill priority={item.priority} />

                {item.completed_at && (
                  <span className="text-xs text-ink-3">
                    {formatTimestamp(item.completed_at)}
                  </span>
                )}

                <span className="flex shrink-0 gap-2 text-xs opacity-100 transition-opacity md:opacity-0 md:group-focus-within:opacity-100 md:group-hover:opacity-100">
                  <button
                    type="button"
                    className="font-medium text-navy hover:underline"
                    onClick={() => void restore(item)}
                  >
                    Restore
                  </button>
                  <button
                    type="button"
                    className="font-medium text-crimson hover:underline"
                    onClick={() => void destroy(item)}
                  >
                    Delete
                  </button>
                </span>
              </li>
            ))}
          </ul>
        )}

        {items.length < total && (
          <div className="border-t border-line px-3 py-2 text-center">
            <button
              type="button"
              className="btn btn-neutral btn-sm"
              onClick={() => setShown((current) => current + PAGE_SIZE)}
            >
              Load {Math.min(PAGE_SIZE, total - items.length)} more
            </button>
          </div>
        )}
      </section>
    </div>
  )
}
